// src/lib/vinculoPlanilhaRateio.ts
//
// Vínculo das UCs às usinas pela planilha de rateio (projeto 12, demanda 03,
// #5838). Quem decide a categoria de cada linha é a RPC
// vincular_ucs_por_planilha_rateio (banco); este módulo só dá nome, ordem e
// forma ao relatório que ela devolve, e normaliza os números do cadastro da
// usina. Sem I/O.

export type CategoriaVinculo =
  | 'vinculavel'
  | 'ja_vinculada'
  | 'vinculada_outra'
  | 'cadastro_nao_aprovado'
  | 'conflito'
  | 'cadastro_duplicado'
  | 'uc_nao_encontrada'
  | 'usina_nao_cadastrada'
  | 'usina_ambigua';

/** Ordem de leitura: primeiro o que será gravado, depois o que precisa de alguém. */
export const ORDEM_CATEGORIAS: CategoriaVinculo[] = [
  'vinculavel',
  'ja_vinculada',
  'vinculada_outra',
  'cadastro_nao_aprovado',
  'conflito',
  'cadastro_duplicado',
  'uc_nao_encontrada',
  'usina_nao_cadastrada',
  'usina_ambigua',
];

export const ROTULO_CATEGORIA: Record<CategoriaVinculo, string> = {
  vinculavel: 'Será vinculada',
  ja_vinculada: 'Já na mesma usina',
  vinculada_outra: 'Em outra usina',
  cadastro_nao_aprovado: 'Cadastro não aprovado',
  conflito: 'Em mais de uma usina na planilha',
  cadastro_duplicado: 'Número repetido no cadastro',
  uc_nao_encontrada: 'UC sem cadastro',
  usina_nao_cadastrada: 'Usina não cadastrada',
  usina_ambigua: 'Número de usina em duas usinas',
};

export const EXPLICACAO_CATEGORIA: Record<CategoriaVinculo, string> = {
  vinculavel: 'UC com cadastro aprovado e sem usina. Ao confirmar, fica ligada à usina da planilha.',
  ja_vinculada: 'Nada a fazer.',
  vinculada_outra: 'Não é trocada automaticamente. Confira e, se for o caso, troque pelo vínculo manual.',
  cadastro_nao_aprovado: 'O cadastro precisa ser aprovado antes. A aprovação não é feita aqui.',
  conflito: 'A mesma UC aparece com usinas diferentes na planilha. Corrija a planilha.',
  cadastro_duplicado: 'O mesmo número está em mais de uma UC do cadastro. Resolva a duplicidade antes.',
  uc_nao_encontrada: 'A UC da planilha não tem cadastro no sistema.',
  usina_nao_cadastrada: 'O número da usina na planilha não bate com nenhuma usina cadastrada.',
  usina_ambigua: 'O número da usina bate com duas usinas do cadastro. Nenhuma UC é ligada até o cadastro ser corrigido.',
};

export type TomCategoria = 'ok' | 'neutro' | 'alerta';

export const TOM_CATEGORIA: Record<CategoriaVinculo, TomCategoria> = {
  vinculavel: 'ok',
  ja_vinculada: 'neutro',
  vinculada_outra: 'alerta',
  cadastro_nao_aprovado: 'alerta',
  conflito: 'alerta',
  cadastro_duplicado: 'alerta',
  uc_nao_encontrada: 'alerta',
  usina_nao_cadastrada: 'alerta',
  usina_ambigua: 'alerta',
};

export interface LinhaVinculo {
  categoria: CategoriaVinculo | string;
  uc: string | null;
  outro_numero_uc: string | null;
  nome: string | null;
  status_planilha: string | null;
  usina_planilha: string | null;
  uc_id: number | null;
  usina_id: string | null;
  usina_nome: string | null;
  usinas_candidatas: string[] | null;
  usina_atual_nome: string | null;
  parceira: boolean | null;
  situacao: string | null;
}

export interface RelatorioVinculo {
  mes_referencia: string;
  planilha_id: string;
  planilha_carregada_em: string | null;
  aplicado: boolean;
  linhas_planilha: number;
  linhas_sem_usina: number;
  contagem: Partial<Record<CategoriaVinculo, number>>;
  vinculadas_agora: number;
  linhas: LinhaVinculo[];
}

const ehCategoria = (c: string): c is CategoriaVinculo => (ORDEM_CATEGORIAS as string[]).includes(c);

/** Contagem na ordem de leitura, sem categorias zeradas. Categoria desconhecida vai para o fim. */
export function contagemOrdenada(contagem: Record<string, number> | null | undefined): { categoria: string; total: number }[] {
  const c = contagem ?? {};
  const conhecidas = ORDEM_CATEGORIAS.filter((k) => (c[k] ?? 0) > 0).map((k) => ({ categoria: k as string, total: c[k] }));
  const outras = Object.keys(c).filter((k) => !ehCategoria(k) && c[k] > 0).map((k) => ({ categoria: k, total: c[k] }));
  return [...conhecidas, ...outras];
}

export function rotuloCategoria(c: string): string {
  return ehCategoria(c) ? ROTULO_CATEGORIA[c] : c;
}

/** Quantas UCs distintas o "Confirmar" vai ligar (a planilha pode repetir a UC na mesma usina). */
export function totalAVincular(linhas: LinhaVinculo[] | null | undefined): number {
  return new Set((linhas ?? []).filter((l) => l.categoria === 'vinculavel' && l.uc_id != null).map((l) => l.uc_id)).size;
}

/** Quantas das linhas a vincular são de UC de empresa parceira (entram, mas aparecem marcadas). */
export function totalParceiraAVincular(linhas: LinhaVinculo[] | null | undefined): number {
  return new Set((linhas ?? []).filter((l) => l.categoria === 'vinculavel' && l.parceira && l.uc_id != null).map((l) => l.uc_id)).size;
}

/** Linhas para o Excel da prévia (cabeçalho + uma linha por item). */
export function linhasExportacao(linhas: LinhaVinculo[] | null | undefined): (string | number)[][] {
  const cab = ['Situação no vínculo', 'UC', 'Outro nº UC', 'Cliente', 'Status na planilha', 'Usina na planilha',
    'Usina do cadastro', 'Usina atual da UC', 'Empresa parceira'];
  const corpo = [...(linhas ?? [])]
    .sort((a, b) => {
      const oa = ORDEM_CATEGORIAS.indexOf(a.categoria as CategoriaVinculo);
      const ob = ORDEM_CATEGORIAS.indexOf(b.categoria as CategoriaVinculo);
      return (oa < 0 ? 99 : oa) - (ob < 0 ? 99 : ob) || (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR');
    })
    .map((l) => [
      rotuloCategoria(l.categoria),
      l.uc ?? '',
      l.outro_numero_uc ?? '',
      l.nome ?? '',
      l.status_planilha ?? '',
      l.usina_planilha ?? '',
      l.usina_nome ?? (l.usinas_candidatas ?? []).join(' / '),
      l.usina_atual_nome ?? '',
      l.parceira ? 'Sim' : '',
    ]);
  return [cab, ...corpo];
}

/**
 * Número de UC da usina como vai para o banco: só dígitos, pontos e traço
 * (mantém a formatação que o usuário digitou, como no cadastro atual);
 * campo vazio ou só com espaço vira null.
 */
export function limparNumeroUsina(v: string | null | undefined): string | null {
  const s = String(v ?? '').trim().replace(/[^\d.\-]/g, '');
  return /\d/.test(s) ? s : null;
}

/** Mesma regra de public.norm_uc: sem pontuação e sem zeros à esquerda. */
export function normUc(v: string | null | undefined): string | null {
  const s = String(v ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
  return s ? s : null;
}

/**
 * Erro de validação do cadastro da usina, ou null. O número antigo tem 11
 * dígitos na planilha; o longo não pode ser igual ao antigo de outra usina
 * (foi o que aconteceu com a "Usina Morrinhos", que levou o número antigo da
 * Vera Cruz 03 no campo longo).
 */
export function validarNumerosUsina(
  longo: string | null,
  antigo: string | null,
  outrasUsinas: { id: string; nome: string; numero_uc_geradora: string | null; numero_uc_geradora_antigo: string | null }[],
  idAtual: string | null,
): string | null {
  const nl = normUc(longo);
  const na = normUc(antigo);
  if (na && !/^\d{8,12}$/.test(na)) return 'O número na planilha de rateio deve ter só dígitos (ex.: 10037129846).';
  if (nl && na && nl === na) return 'O número longo e o número da planilha não podem ser iguais: o longo é o que aparece na fatura.';
  for (const u of outrasUsinas) {
    if (u.id === idAtual) continue;
    const ol = normUc(u.numero_uc_geradora);
    const oa = normUc(u.numero_uc_geradora_antigo);
    if (na && (na === oa || na === ol)) return `O número da planilha ${antigo} já pertence à ${u.nome}.`;
    if (nl && (nl === ol || nl === oa)) return `O número longo ${longo} já pertence à ${u.nome}.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Capacidade instalada: cadastro × telemetria
// ---------------------------------------------------------------------------

/**
 * A telemetria (`unidades_geradoras.capacidade_instalada`) guarda MW; o cadastro
 * da usina guarda kWp. Conferido em 22/09/2026: o Bloco 3 (Vera Cruz 03) marca
 * 1,36 = 1.360 kWp, e a geração dos últimos 6 meses confirma a proporção entre
 * as usinas (a Vera Cruz 05, de 0,68, gera exatamente metade da 01).
 */
export function kwpDaTelemetria(capacidadeMw: number | string | null | undefined): number | null {
  const n = typeof capacidadeMw === 'number' ? capacidadeMw : parseFloat(String(capacidadeMw ?? '').replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 1000);
}

/** Diferença relevante entre o cadastro e a telemetria (5% de tolerância). */
export function capacidadeDivergente(cadastroKwp: number | null | undefined, telemetriaKwp: number | null): boolean {
  if (telemetriaKwp === null) return false;
  if (cadastroKwp == null) return true;
  return Math.abs(cadastroKwp - telemetriaKwp) > telemetriaKwp * 0.05;
}
